<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, ref } from "vue"
import type { BuildingVisibleToEntry } from "@types"

defineOptions({
    name: "ItemFolder",
})

// ================================
// TYPES & INTERFACES
// ================================
export interface ItemFolderData {
    _id: string
    name?: string
    /** Personnes ayant accès (aligné `access` / utilisateurs projetés côté API). */
    visible_to?: BuildingVisibleToEntry[]
}

interface Props {
    /**
     * Données du dossier à afficher
     */
    folder: ItemFolderData
    /**
     * Racine `<button type="button">` pour le même clic natif que les lignes dashboard.
     */
    nativeButton?: boolean
    /**
     * Tooltip sur le nom (ex. désactivé tant que le texte n’est pas ellipsé).
     */
    nameTooltip?: { value: string; disabled: boolean }
}

interface Emits {
    /**
     * Clic sur la ligne dossier
     */
    (e: "click", folderId: string, ev?: MouseEvent): void
}

type AccessAvatarSlot = {
    key: string
    image: string | undefined
    label: string | undefined
    ariaLabel: string
    entry: BuildingVisibleToEntry
}

// ================================
// PROPS & EMITS
// ================================
const props = withDefaults(defineProps<Props>(), {
    nativeButton: false,
    nameTooltip: undefined,
})
const emit = defineEmits<Emits>()

const VISIBLE_TO_OVERFLOW_THRESHOLD = 4
const VISIBLE_TO_HEAD_COUNT_BEFORE_OVERFLOW = 3

// ================================
// STATE
// ================================
/**
 * Élément du nom (ellipse / tooltip) pour le parent (liste dashboard).
 */
const nameSpanRef = ref<HTMLElement | null>(null)

defineExpose({
    nameSpanRef,
})

// ================================
// COMPUTED
// ================================
/**
 * Liste des personnes visibles pour le dossier (accès).
 */
const getVisibleToEntries = computed(() => props.folder.visible_to ?? [])

/**
 * Affiche la rangée d’avatars si au moins un accès est défini.
 */
const getShowAccessAvatars = computed(
    () => getVisibleToEntries.value.length > 0
)

/**
 * Slots d’avatars (+ débordement « +N ») pour l’affichage compact.
 */
const getAccessAvatarDisplay = computed(() => {
    const entries = getVisibleToEntries.value
    const n = entries.length
    if (n === 0) {
        return { slots: [] as AccessAvatarSlot[], overflowExtra: 0 }
    }
    if (n <= VISIBLE_TO_OVERFLOW_THRESHOLD) {
        return {
            slots: entries.map((e, i) => toAccessAvatarSlot(e, i)),
            overflowExtra: 0,
        }
    }
    const head = entries.slice(0, VISIBLE_TO_HEAD_COUNT_BEFORE_OVERFLOW)
    return {
        slots: head.map((e, i) => toAccessAvatarSlot(e, i)),
        overflowExtra: n - VISIBLE_TO_HEAD_COUNT_BEFORE_OVERFLOW,
    }
})

/**
 * Texte du tooltip pour l’avatar « +N » (personnes au-delà du seuil).
 */
const getAccessOverflowTooltip = computed(() => {
    const entries = getVisibleToEntries.value
    if (entries.length <= VISIBLE_TO_OVERFLOW_THRESHOLD) {
        return ""
    }
    return entries
        .slice(VISIBLE_TO_HEAD_COUNT_BEFORE_OVERFLOW)
        .map((e) => buildAccessPersonTooltipText(e))
        .join("\n\n")
})

// ================================
// BUSINESS LOGIC
// ================================
/**
 * Nom affichable pour une entrée d’accès (prénom + nom).
 * @param entry - Entrée projetée côté API
 * @returns Nom complet trimé
 */
function accessPersonFullName(entry: BuildingVisibleToEntry): string {
    const fn = (entry.firstname ?? "").trim()
    const ln = (entry.lastname ?? "").trim()
    return [fn, ln].filter(Boolean).join(" ").trim()
}

/**
 * Texte du tooltip (nom + email) pour un collaborateur.
 * @param entry - Entrée accès
 * @returns Texte multi-lignes pour `v-tooltip`
 */
function buildAccessPersonTooltipText(entry: BuildingVisibleToEntry): string {
    const name = accessPersonFullName(entry)
    const email = (entry.username ?? "").trim()
    if (name && email) {
        return `${name}\n${email}`
    }
    if (email) {
        return email
    }
    return name || resolveAccessPersonLabel(entry)
}

/**
 * Options passées à `v-tooltip` (directive) pour le contenu personnalisé.
 * @param value - Texte du tooltip
 * @returns Objet `{ value, class }` ou tooltip désactivé
 */
function buildAccessTooltipDirectiveOptions(value: string) {
    const v = value.trim()
    if (!v) {
        return { disabled: true as const }
    }
    return {
        value: v,
        class: "item-folder-access-tooltip",
    }
}

/**
 * Libellé accessible (nom, email ou identifiant) pour une entrée.
 * @param entry - Entrée accès
 * @returns Libellé pour `aria-label` / secours
 */
function resolveAccessPersonLabel(entry: BuildingVisibleToEntry): string {
    const name = accessPersonFullName(entry)
    if (name) return name
    if (entry.username) return entry.username
    return entry._id_user ?? entry._id ?? "Utilisateur"
}

/**
 * Couleur de fond du cercle avatar (variation par index).
 * @param index - Index dans la liste (ou -1 pour overflow)
 * @returns Couleur hex
 */
function pickAccessAvatarCircleColor(index: number): string {
    const a = index === -1 ? 0 : index % 4
    switch (a) {
        case 1:
            return "#DADDE9"
        case 2:
            return "#FFD8BC"
        case 3:
            return "#FFEABC"
        default:
            return "#A2E4CF"
    }
}

/**
 * PassThrough PrimeVue pour le style du label avatar (cercle coloré).
 * @param index - Index de l’avatar
 * @returns Objet `pt` pour `BaseAvatar`
 */
function buildAccessAvatarLabelPt(index: number) {
    return {
        root: {
            style: {
                backgroundColor: pickAccessAvatarCircleColor(index),
            },
        },
    }
}

/**
 * Initiales affichées sur l’avatar si pas d’image.
 * @param entry - Entrée accès
 * @returns Deux caractères ou secours
 */
function accessPersonInitials(entry: BuildingVisibleToEntry): string {
    const fn = (entry.firstname ?? "").trim()
    const ln = (entry.lastname ?? "").trim()
    if (fn && ln) {
        return `${fn.charAt(0)}${ln.charAt(0)}`.toUpperCase()
    }
    if (fn.length >= 2) return fn.slice(0, 2).toUpperCase()
    if (fn.length === 1) return `${fn.charAt(0)}`.toUpperCase()
    const u = (entry.username ?? "").trim()
    if (u.includes("@")) {
        const local = u.split("@")[0] ?? ""
        if (local.length >= 2) return local.slice(0, 2).toUpperCase()
        if (local.length === 1) return local.toUpperCase()
    } else if (u.length >= 2) {
        return u.slice(0, 2).toUpperCase()
    }
    const id = entry._id_user ?? entry._id ?? ""
    const hex = id.replace(/[^a-fA-F0-9]/g, "")
    if (hex.length >= 2) return hex.slice(-2).toUpperCase()
    return "?"
}

/**
 * Construit un slot avatar (clé, image, initiales, aria) pour le groupe.
 * @param entry - Entrée accès
 * @param index - Position dans la liste affichée
 * @returns Données pour un `BaseAvatar`
 */
function toAccessAvatarSlot(
    entry: BuildingVisibleToEntry,
    index: number
): AccessAvatarSlot {
    const id = entry._id_user ?? entry._id ?? ""
    const key = id || `visible-to-${index}`
    const img = entry.img?.trim()
    const image = img ? img : undefined
    const ariaLabel = resolveAccessPersonLabel(entry)
    return {
        key,
        image,
        label: image ? undefined : accessPersonInitials(entry),
        ariaLabel,
        entry,
    }
}

// ================================
// INTERACTIONS
// ================================
/**
 * Émet le clic sur la ligne dossier (id + événement pour la navigation / DnD).
 * @param ev - Clic utilisateur
 */
function onClick(ev: MouseEvent): void {
    emit("click", props.folder._id, ev)
}
</script>

<template>
    <!-- ================================ -->
    <!-- LIGNE RÉCAP DOSSIER -->
    <!-- ================================ -->
    <component
        :is="props.nativeButton ? 'button' : 'div'"
        :type="props.nativeButton ? 'button' : undefined"
        class="item-folder flex cursor-pointer items-start gap-2.5 py-0.5 hover:text-primary"
        :class="
            props.nativeButton
                ? 'm-0 w-full min-w-0 border-0 bg-transparent p-0 text-left font-inherit text-inherit'
                : ''
        "
        @click="onClick"
    >
        <i
            class="pi pi-folder shrink-0 text-surface-600"
            style="font-size: 1.75rem"
            aria-hidden="true"
        />
        <div class="flex min-w-0 flex-1 flex-col gap-1">
            <span
                ref="nameSpanRef"
                v-tooltip.top="nameTooltip ?? { disabled: true }"
                class="min-w-0 truncate font-medium"
            >
                {{ props.folder.name || "-" }}
            </span>
        </div>
        <!-- ================================ -->
        <!-- ACCÈS (AVATARS) -->
        <!-- ================================ -->
        <BaseAvatarGroup
            v-if="getShowAccessAvatars"
            class="item-folder__access-avatars shrink-0"
            @click.stop
        >
            <BaseAvatar
                v-for="(slot, slotIndex) in getAccessAvatarDisplay.slots"
                :key="slot.key"
                v-tooltip.top="
                    buildAccessTooltipDirectiveOptions(
                        buildAccessPersonTooltipText(slot.entry)
                    )
                "
                :image="slot.image"
                :label="slot.label"
                :aria-label="slot.ariaLabel"
                :pt="
                    slot.image ? undefined : buildAccessAvatarLabelPt(slotIndex)
                "
                shape="circle"
                size="normal"
            />
            <BaseAvatar
                v-if="getAccessAvatarDisplay.overflowExtra > 0"
                v-tooltip.top="
                    buildAccessTooltipDirectiveOptions(getAccessOverflowTooltip)
                "
                :label="`+${getAccessAvatarDisplay.overflowExtra}`"
                :pt="
                    buildAccessAvatarLabelPt(
                        getAccessAvatarDisplay.slots.length
                    )
                "
                shape="circle"
                size="normal"
                :aria-label="getAccessOverflowTooltip"
            />
        </BaseAvatarGroup>
    </component>
</template>

<!-- Styles limités : taille avatars / tooltip PrimeVue (portail), non exprimables proprement en Tailwind seul. -->
<style scoped>
.item-folder__access-avatars :deep(.p-avatar) {
    --p-avatar-size: 2rem;
    --p-avatar-font-size: 0.8rem;
    width: var(--p-avatar-size);
    height: var(--p-avatar-size);
    min-width: var(--p-avatar-size);
    min-height: var(--p-avatar-size);
    font-size: var(--p-avatar-font-size);
}

.item-folder__access-avatars :deep(.p-avatar-label) {
    line-height: 1;
    font-size: inherit;
}

.item-folder__access-avatars :deep(.p-avatar-image) {
    object-fit: cover;
}
</style>

<style>
.item-folder-access-tooltip .p-tooltip-text {
    max-width: min(22rem, 85vw);
    text-align: left;
    white-space: pre-line;
}
</style>
